/**
 * A flow's live-data contract, read off its compiled Flow JSON: every point where the flow calls
 * the owner's server, exactly what it sends, and what the server may reply with.
 *
 * FlowGate forwards each call to the owner's server as the plain JSON WhatsApp sent us (see
 * booking/006 and docs/dynamic-flows.md), so the contract is WhatsApp's data-exchange contract for
 * THIS flow: its screens, its fields, its routes. Generated from the flow as it stands, every time,
 * never stored, so it cannot drift from the flow.
 *
 * Pure: no React, no DOM, no Node. The builder renders it as a page; the same function can run in
 * the JVM through the flow-core bundle.
 */

const FORM_REF = /^\$\{\s*form\.([A-Za-z0-9_]+)\s*\}$/;
const SCREEN_FORM_REF = /^\$\{\s*screen\.([A-Za-z0-9_]+)\.form\.([A-Za-z0-9_]+)\s*\}$/;
const DATA_REF = /^\$\{\s*data\.([A-Za-z0-9_]+)\s*\}$/;
const ANY_REF = /\$\{[^}]+\}/;

const CONTAINER_KEYS = ['then', 'else'];

/** Every component on a screen, including those inside If / Switch branches. */
const walk = (children, visit) => {
  (children || []).forEach((child) => {
    if (!child || typeof child !== 'object') return;
    visit(child);
    CONTAINER_KEYS.forEach((key) => walk(child[key], visit));
    if (child.cases && typeof child.cases === 'object') {
      Object.values(child.cases).forEach((branch) => walk(branch, visit));
    }
  });
};

const optionIds = (dataSource) => (Array.isArray(dataSource)
  ? dataSource.map((option) => option && option.id).filter((id) => typeof id === 'string')
  : []);

/** The JSON a form field arrives as, by component. What WhatsApp sends for each input type. */
const schemaOfField = (component) => {
  const type = component && component.type;
  const ids = optionIds(component && component['data-source']);
  const withEnum = (schema) => (ids.length ? { ...schema, enum: ids } : schema);
  switch (type) {
    case 'TextInput':
      return component['input-type'] === 'number' ? { type: 'number' } : { type: 'string' };
    case 'TextArea':
      return { type: 'string' };
    case 'Dropdown':
    case 'RadioButtonsGroup':
      return withEnum({ type: 'string' });
    case 'CheckboxGroup':
    case 'ChipsSelector':
      return { type: 'array', items: withEnum({ type: 'string' }) };
    case 'DatePicker':
      return { type: 'string', format: 'date' };
    case 'CalendarPicker':
      return component.mode === 'range'
        ? {
          type: 'object',
          properties: { 'start-date': { type: 'string', format: 'date' }, 'end-date': { type: 'string', format: 'date' } },
        }
        : { type: 'string', format: 'date' };
    case 'OptIn':
      return { type: 'boolean' };
    case 'PhotoPicker':
    case 'DocumentPicker':
      return {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            media_id: { type: 'string' },
            cdn_url: { type: 'string' },
            file_name: { type: 'string' },
            encryption_metadata: { type: 'object' },
          },
        },
      };
    default:
      return { type: 'string' };
  }
};

/** A screen's `data` declaration as JSON Schema, with its preview examples moved to `example`. */
const schemaOfDeclaration = (declaration) => {
  if (!declaration || typeof declaration !== 'object') return { type: 'string' };
  const { __example__: example, ...rest } = declaration;
  const schema = { ...rest };
  if (schema.items) schema.items = schemaOfDeclaration(schema.items);
  if (schema.properties) {
    schema.properties = Object.fromEntries(
      Object.entries(schema.properties).map(([key, value]) => [key, schemaOfDeclaration(value)]));
  }
  if (example !== undefined) schema.example = example;
  return schema;
};

const exampleOf = (schema, name) => {
  if (!schema) return null;
  if (schema.example !== undefined) return schema.example;
  if (schema.const !== undefined) return schema.const;
  if (schema.enum && schema.enum.length) return schema.enum[0];
  switch (schema.type) {
    case 'number': return 1;
    case 'boolean': return true;
    case 'array': return schema.items ? [exampleOf(schema.items, name)] : [];
    case 'object':
      return Object.fromEntries(Object.entries(schema.properties || {}).map(([key, value]) => [key, exampleOf(value, key)]));
    default:
      return schema.format === 'date' ? '2026-10-01' : `example ${String(name || 'value').replace(/_/g, ' ')}`;
  }
};

/** Where a payload value comes from, in words, and the JSON it arrives as. */
const describeValue = (value, screen, fieldsByScreen, dataByScreen) => {
  if (typeof value !== 'string') {
    return { schema: { type: typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'object', const: value }, source: 'a fixed value' };
  }
  let match = FORM_REF.exec(value);
  if (match) {
    const field = fieldsByScreen[screen.id]?.[match[1]];
    return {
      schema: field ? schemaOfField(field) : { type: 'string' },
      source: field ? `what the customer entered in "${field.label || match[1]}" (${field.type})` : `form field ${match[1]}`,
    };
  }
  match = SCREEN_FORM_REF.exec(value);
  if (match) {
    const field = fieldsByScreen[match[1]]?.[match[2]];
    return {
      schema: field ? schemaOfField(field) : { type: 'string' },
      source: field ? `"${field.label || match[2]}" from screen ${match[1]} (${field.type})` : `form field ${match[2]} on screen ${match[1]}`,
    };
  }
  match = DATA_REF.exec(value);
  if (match) {
    const declared = dataByScreen[screen.id]?.[match[1]];
    return {
      schema: declared ? schemaOfDeclaration(declared) : { type: 'string' },
      source: `the "${match[1]}" value your server sent for this screen`,
    };
  }
  if (ANY_REF.test(value)) return { schema: { type: 'string' }, source: 'text with answers filled in' };
  return { schema: { type: 'string', const: value }, source: 'a fixed value' };
};

const labelOf = (component) => component.label || component.text || component.type;

/**
 * @param {object} flowJson compiled Flow JSON (what is uploaded to Meta)
 * @returns {{
 *   dynamic: boolean,
 *   exchanges: Array<{screenId: string, screenTitle: string, trigger: string,
 *     fields: Array<{name: string, source: string, schema: object}>,
 *     request: object, replies: Array<{screenId: string, screenTitle: string, sameScreen: boolean,
 *     fields: Array<{name: string, schema: object}>, example: object}>}>,
 *   backScreens: string[],
 * }}
 */
export const describeLiveDataContract = (flowJson) => {
  const screens = Array.isArray(flowJson?.screens) ? flowJson.screens : [];
  const titles = Object.fromEntries(screens.map((screen) => [screen.id, screen.title || screen.id]));
  const fieldsByScreen = {};
  const dataByScreen = {};
  screens.forEach((screen) => {
    const fields = {};
    walk(screen.layout?.children, (component) => {
      if (typeof component.name === 'string') fields[component.name] = component;
    });
    fieldsByScreen[screen.id] = fields;
    dataByScreen[screen.id] = screen.data && typeof screen.data === 'object' ? screen.data : {};
  });

  // What a screen reads from its data: declared fields, plus any ${data.x} it uses without
  // declaring (a Dropdown's options, a heading), so a reply lists everything the screen needs.
  const usedData = (screenId) => {
    const screen = screens.find((candidate) => candidate.id === screenId);
    const used = {};
    walk(screen?.layout?.children, (component) => {
      Object.entries(component).forEach(([key, value]) => {
        const match = typeof value === 'string' ? DATA_REF.exec(value) : null;
        if (!match) return;
        used[match[1]] = key === 'data-source' || key === 'list-items'
          ? { type: 'array', items: { type: 'object', properties: { id: { type: 'string' }, title: { type: 'string' } }, required: ['id', 'title'] } }
          : { type: 'string' };
      });
    });
    return used;
  };

  const replyTo = (screenId) => {
    const declared = dataByScreen[screenId] || {};
    const fields = [
      ...Object.entries(declared).map(([name, declaration]) => ({ name, schema: schemaOfDeclaration(declaration) })),
      ...Object.entries(usedData(screenId))
        .filter(([name]) => !(name in declared))
        .map(([name, schema]) => ({ name, schema })),
    ];
    return {
      screenId,
      screenTitle: titles[screenId] || screenId,
      fields,
      example: { screen: screenId, data: Object.fromEntries(fields.map((field) => [field.name, exampleOf(field.schema, field.name)])) },
    };
  };

  const exchanges = [];
  screens.forEach((screen) => {
    walk(screen.layout?.children, (component) => {
      const actions = [];
      ['on-click-action', 'on-select-action'].forEach((key) => {
        if (component[key]?.name === 'data_exchange') actions.push({ action: component[key], trigger: `${labelOf(component)}` });
      });
      (Array.isArray(component['list-items']) ? component['list-items'] : []).forEach((item) => {
        if (item?.['on-click-action']?.name === 'data_exchange') {
          actions.push({ action: item['on-click-action'], trigger: `${item['main-content']?.title || item.id || 'a list item'} in ${labelOf(component)}` });
        }
      });
      actions.forEach(({ action, trigger }) => {
        const payload = action.payload && typeof action.payload === 'object' ? action.payload : {};
        const fields = Object.entries(payload).map(([name, value]) => ({
          name, ...describeValue(value, screen, fieldsByScreen, dataByScreen),
        }));
        const routed = flowJson?.routing_model?.[screen.id];
        const next = Array.isArray(routed) && routed.length
          ? routed
          : screens.map((candidate) => candidate.id).filter((id) => id !== screen.id);
        const replies = [...next.map((id) => ({ ...replyTo(id), sameScreen: false })),
          ...(dataByScreen[screen.id]?.error_message ? [{ ...replyTo(screen.id), sameScreen: true }] : [])];
        exchanges.push({
          screenId: screen.id,
          screenTitle: titles[screen.id],
          trigger,
          fields,
          request: {
            version: '3.0',
            action: 'data_exchange',
            screen: screen.id,
            data: Object.fromEntries(fields.map((field) => [field.name, exampleOf(field.schema, field.name)])),
            flow_token: 'flow-token-from-whatsapp',
          },
          replies,
        });
      });
    });
  });

  return {
    dynamic: exchanges.length > 0,
    exchanges,
    backScreens: screens.filter((screen) => screen.refresh_on_back === true).map((screen) => screen.id),
  };
};

const objectSchema = (fields) => ({
  type: 'object',
  properties: Object.fromEntries(fields.map((field) => [field.name, field.schema])),
  required: fields.map((field) => field.name),
});

/**
 * The contract as an OpenAPI 3.1 document: one POST, a request per exchange, the replies each one
 * may send. The signature and timestamp headers are FlowGate's (the same scheme as its webhooks).
 */
export const buildOpenApiSpec = (flowJson, { flowName = 'Flow', flowId = '' } = {}) => {
  const contract = describeLiveDataContract(flowJson);
  const requests = contract.exchanges.map((exchange) => ({
    title: `${exchange.screenTitle}: ${exchange.trigger}`,
    type: 'object',
    properties: {
      version: { type: 'string', const: '3.0' },
      action: { type: 'string', const: 'data_exchange' },
      screen: { type: 'string', const: exchange.screenId },
      data: objectSchema(exchange.fields),
      flow_token: { type: 'string' },
    },
    required: ['version', 'action', 'screen', 'data', 'flow_token'],
    example: exchange.request,
  }));
  const replies = [];
  const seen = new Set();
  contract.exchanges.forEach((exchange) => exchange.replies.forEach((reply) => {
    if (seen.has(reply.screenId)) return;
    seen.add(reply.screenId);
    replies.push({
      title: `Show ${reply.screenTitle}`,
      type: 'object',
      properties: { screen: { type: 'string', const: reply.screenId }, data: objectSchema(reply.fields) },
      required: ['screen', 'data'],
      example: reply.example,
    });
  }));
  replies.push({
    title: 'Finish the flow',
    type: 'object',
    properties: {
      screen: { type: 'string', const: 'SUCCESS' },
      data: {
        type: 'object',
        properties: {
          extension_message_response: {
            type: 'object',
            properties: { params: { type: 'object', properties: { flow_token: { type: 'string' } }, additionalProperties: true } },
          },
        },
      },
    },
    required: ['screen', 'data'],
    example: { screen: 'SUCCESS', data: { extension_message_response: { params: { flow_token: 'flow-token-from-whatsapp' } } } },
  });

  return {
    openapi: '3.1.0',
    info: {
      title: `${flowName}: live data`,
      version: '1.0.0',
      description: 'What FlowGate sends your server while a customer is in this flow, and what your '
        + 'server may reply. Requests are the plain JSON WhatsApp sent, decrypted by FlowGate; '
        + 'replies are encrypted by FlowGate. Answer within 8 seconds.'
        + (flowId ? ` Flow ${flowId}.` : ''),
    },
    paths: {
      '/': {
        post: {
          summary: 'A step of the flow that needs your server',
          parameters: [
            { name: 'X-FlowGate-Signature', in: 'header', required: true, description: 'sha256=<hex HMAC-SHA256 of the raw body, keyed by the flow\'s signing secret>', schema: { type: 'string' } },
            { name: 'X-FlowGate-Timestamp', in: 'header', required: true, description: 'Unix seconds when FlowGate sent the request', schema: { type: 'string' } },
            { name: 'X-FlowGate-Flow', in: 'header', required: true, description: 'The FlowGate flow id', schema: { type: 'string' } },
          ],
          requestBody: {
            required: true,
            content: { 'application/json': { schema: requests.length === 1 ? requests[0] : { oneOf: requests } } },
          },
          responses: {
            200: {
              description: 'The next screen and its data, an error on the same screen, or the end of the flow.',
              content: { 'application/json': { schema: { oneOf: replies } } },
            },
          },
        },
      },
    },
  };
};
